import { useState } from 'react';
import { useForm, type Control } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, Lock, KeyRound, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { ACCENT_MARK_CLASS } from '@/styles/accentMark';
import { RAISED_CARD_CLASS } from '@/styles/raisedCard';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useChangePassword } from '../hooks/useChangePassword';
import { changePasswordSchema, type ChangePasswordPayload } from '../types/auth.types';

interface PasswordFieldProps {
  control: Control<ChangePasswordPayload>;
  name: keyof ChangePasswordPayload;
  label: string;
  placeholder: string;
  helperText?: string;
}

function PasswordField({ control, name, label, placeholder, helperText }: PasswordFieldProps) {
  const [show, setShow] = useState(false);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel className="text-xs">{label}</FormLabel>
          <FormControl>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                <Lock className="size-3.5" />
              </div>
              <Input
                type={show ? 'text' : 'password'}
                placeholder={placeholder}
                className="h-10 pl-9 pr-10 text-sm"
                {...field}
              />
              <button
                type="button"
                onClick={() => setShow((prev) => !prev)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                tabIndex={-1}
              >
                {show ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
              </button>
            </div>
          </FormControl>
          {helperText && <FormDescription>{helperText}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export function ChangePasswordForm() {
  const [isOpen, setIsOpen] = useState(false);

  const form = useForm<ChangePasswordPayload>({
    resolver: zodResolver(changePasswordSchema),
    mode: 'onTouched',
    defaultValues: { oldPassword: '', newPassword: '', confirmPassword: '' },
  });

  const { mutate, isPending } = useChangePassword(() => {
    setIsOpen(false);
    form.reset();
  });

  function handleChangePassword(values: ChangePasswordPayload) {
    mutate({ oldPassword: values.oldPassword, newPassword: values.newPassword });
  }

  function handleCancel() {
    setIsOpen(false);
    form.reset();
  }

  return (
    <Card id="change-password" className={RAISED_CARD_CLASS}>
      <CardContent>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className={cn(
                'flex size-9 items-center justify-center rounded-lg',
                ACCENT_MARK_CLASS,
              )}
            >
              <KeyRound className="size-4" />
            </div>
            <div>
              <h3 className="text-sm font-semibold">Password & Security</h3>
              <p className="text-2xs text-muted-foreground">Update your account password</p>
            </div>
          </div>
          <Button
            variant={isOpen ? 'outline' : 'default'}
            size="sm"
            onClick={isOpen ? handleCancel : () => setIsOpen(true)}
            className="text-xs"
          >
            {isOpen ? 'Cancel' : 'Change Password'}
          </Button>
        </div>

        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CollapsibleContent className="overflow-hidden h-(--collapsible-panel-height) transition-[height] duration-300 ease-out data-starting-style:h-0 data-ending-style:h-0">
            <div className="mt-5 border-t pt-5">
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(handleChangePassword)}
                  className="flex flex-col gap-4 max-w-md mx-auto"
                >
                  <PasswordField
                    control={form.control}
                    name="oldPassword"
                    label="Current Password"
                    placeholder="Enter current password"
                  />
                  <PasswordField
                    control={form.control}
                    name="newPassword"
                    label="New Password"
                    placeholder="Enter new password"
                    helperText="Minimum 6 characters"
                  />
                  <PasswordField
                    control={form.control}
                    name="confirmPassword"
                    label="Confirm Password"
                    placeholder="Re-enter new password"
                  />
                  <Button type="submit" disabled={isPending} className="w-full h-10 mt-2">
                    {isPending ? (
                      <>
                        <Loader2 className="size-4 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      'Update Password'
                    )}
                  </Button>
                </form>
              </Form>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
}
